import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import {
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider,
	Box
} from '@mui/material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';

import { DataContext } from '../../../context/DataContext';
import { getRol, getToken, getUser } from '../../../api/Account';
import { getCreditsByBarrioAsync } from '../../../api/Report';
import { getGestoresBySucursalAccessAsync, getSucursalByUserAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { EncabezadoOrdenable, useOrdenTabla } from '../../../helpers/OrdenTabla';
import NoData from '../../../components/NoData';

const TODOS = 't';

const moneda = new Intl.NumberFormat('es-NI', { style: 'currency', currency: 'NIO' });
const importe = (valor) => moneda.format(valor ?? 0);
const fecha = (valor) => moment(valor).format('L');

/**
 * Definición única de las columnas: la usan la tabla, los subtotales y el Excel.
 * Tener una sola fuente evita que la exportación y lo que se ve en pantalla se
 * desincronicen cuando se agrega o se quita una columna.
 *
 * - `total: true` marca las columnas que se suman por barrio y en el total general.
 * - `excel` es el valor crudo (sin formato) que va al archivo, para que Excel lo
 *   trate como número o fecha y no como texto.
 */
const COLUMNAS = [
	{ key: 'id', label: '#', align: 'center', valor: (c) => c.id, excel: (c) => c.id },
	{ key: 'fullName', label: 'Cliente', align: 'left', valor: (c) => c.fullName },
	{ key: 'address', label: 'Direccion', align: 'left', valor: (c) => c.address },
	{
		key: 'fechaEntrega',
		label: 'Desembolso',
		align: 'center',
		valor: (c) => fecha(c.fechaEntrega)
	},
	{
		key: 'fechaVencimiento',
		label: 'Vencimiento',
		align: 'center',
		valor: (c) => fecha(c.fechaVencimiento)
	},
	{
		key: 'montoEntregado',
		label: 'Monto Entregado',
		align: 'right',
		total: true,
		valor: (c) => importe(c.montoEntregado),
		excel: (c) => c.montoEntregado
	},
	{
		key: 'saldoPrincipal',
		label: 'Saldo Principal',
		align: 'right',
		total: true,
		valor: (c) => importe(c.saldoPrincipal),
		excel: (c) => c.saldoPrincipal
	},
	{
		key: 'saldoInteresCorriente',
		label: 'Saldo Int. Corriente',
		align: 'right',
		total: true,
		valor: (c) => importe(c.saldoInteresCorriente),
		excel: (c) => c.saldoInteresCorriente
	},
	{
		key: 'montoAbonado',
		label: 'Monto Abonado',
		align: 'right',
		total: true,
		valor: (c) => importe(c.montoAbonado),
		excel: (c) => c.montoAbonado
	},
	{
		key: 'diasAtraso',
		label: 'Dias Atraso',
		align: 'center',
		valor: (c) => c.diasAtraso,
		excel: (c) => c.diasAtraso
	},
	{
		key: 'montoAtraso',
		label: 'Monto en Atraso',
		align: 'right',
		total: true,
		valor: (c) => importe(c.montoAtraso),
		excel: (c) => c.montoAtraso
	}
];

const COLUMNAS_TOTALIZADAS = COLUMNAS.filter((col) => col.total);

// Las columnas anteriores a la primera totalizada se fusionan en una sola celda
// con la etiqueta del total; de ahí en adelante cada columna lleva su suma.
const PRIMERA_COLUMNA_TOTALIZADA = COLUMNAS.findIndex((col) => col.total);
const COLUMNAS_PIE = COLUMNAS.slice(PRIMERA_COLUMNA_TOTALIZADA);

const sumarColumnas = (creditos) =>
	COLUMNAS_TOTALIZADAS.reduce((acumulado, col) => {
		acumulado[col.key] = creditos.reduce((suma, c) => suma + (c[col.key] ?? 0), 0);
		return acumulado;
	}, {});

const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @page { size: landscape; margin: 5mm; }
`;

const CreditsByBarrio = () => {
	const [data, setData] = useState([]);
	const [sucursalName, setSucursalName] = useState('Todas');
	const [gestorName, setGestorName] = useState('Todos');

	const compRef = useRef();
	const { selectedSucursal, selectedGestor, selectedBarrio } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const { orden, cambiarOrden, ordenar } = useOrdenTabla('fullName', [
		'fullName',
		'address',
		'fechaEntrega',
		'fechaVencimiento'
	]);

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);

			const [reporte, sucursales, gestores] = await Promise.all([
				getCreditsByBarrioAsync(token, {
					sucursalId: selectedSucursal === TODOS ? 0 : Number(selectedSucursal),
					gestorId: selectedGestor === TODOS ? 0 : Number(selectedGestor),
					communityId: selectedBarrio === TODOS ? 0 : Number(selectedBarrio)
				}),
				getSucursalByUserAsync(token),
				getGestoresBySucursalAccessAsync(token)
			]);

			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (selectedSucursal !== TODOS && sucursales.statusResponse) {
				const suc = sucursales.data.result.find(
					(s) => s.id.toString() === selectedSucursal
				);
				setSucursalName(suc ? suc.description : selectedSucursal);
			}
			if (selectedGestor !== TODOS && gestores.statusResponse) {
				const ges = gestores.data.result.find((g) => g.id.toString() === selectedGestor);
				setGestorName(ges ? ges.fullName : selectedGestor);
			}
		})();
	}, [
		token,
		selectedSucursal,
		selectedGestor,
		selectedBarrio,
		setIsLoading,
		manejarError
	]);

	// Agrupación y subtotales se derivan de los datos, no se acumulan durante el
	// render: así el reporte no cambia si React vuelve a renderizar.
	const barrios = useMemo(() => {
		const porBarrio = new Map();
		data.forEach((credito) => {
			const nombre = credito.barrio;
			if (!porBarrio.has(nombre)) {
				porBarrio.set(nombre, []);
			}
			porBarrio.get(nombre).push(credito);
		});

		return Array.from(porBarrio, ([nombre, creditos]) => ({
			nombre,
			creditos,
			totales: sumarColumnas(creditos)
		})).sort((a, b) => a.nombre.localeCompare(b.nombre));
	}, [data]);

	const totalGeneral = useMemo(() => sumarColumnas(data), [data]);

	const barrioName = useMemo(() => {
		if (selectedBarrio === TODOS) return 'Todos';
		return isEmpty(data) ? '-' : data[0].barrio;
	}, [selectedBarrio, data]);

	const exportExcel = () => {
		const filas = data.map((credito) => {
			const fila = { barrio: credito.barrio };
			COLUMNAS.forEach((col) => {
				fila[col.label] = col.excel ? col.excel(credito) : col.valor(credito);
			});
			return fila;
		});

		const libro = XLSX.utils.book_new();
		const hoja = XLSX.utils.json_to_sheet(filas);
		XLSX.utils.book_append_sheet(libro, hoja, 'CreditosPorBarrio');
		XLSX.writeFile(libro, 'CreditosPorBarrio.xlsx');
	};

	const FilaTotales = ({ etiqueta, totales, resaltado }) => (
		<TableRow>
			<TableCell
				colSpan={PRIMERA_COLUMNA_TOTALIZADA}
				align="right"
				sx={{ fontWeight: 'bold', fontSize: 10 }}
			>
				{etiqueta}
			</TableCell>
			{COLUMNAS_PIE.map((col) => (
				<TableCell
					key={col.key}
					align={col.align}
					sx={{
						fontWeight: 'bold',
						fontSize: 10,
						color: resaltado ? 'primary.main' : 'text.primary'
					}}
				>
					{col.total ? importe(totales[col.key]) : ''}
				</TableCell>
			))}
		</TableRow>
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton onClick={() => exportExcel()} sx={{ color: 'primary.main' }}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 22, width: 22 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: 'text.secondary',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: 'text.secondary',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte de Créditos por Barrio
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => (
								<IconButton sx={{ color: 'info.main' }}>
									<PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
								</IconButton>
							)}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					<Box sx={{ paddingX: '22px', textAlign: 'center' }}>
						<Stack direction={'row'} justifyContent="space-between">
							<Stack>
								<Typography
									sx={{ color: 'text.secondary', textAlign: 'left' }}
									fontSize={11}
								>
									Parametros:
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{`Sucursal: ${sucursalName}`}
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{`Gestor: ${gestorName}`}
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{`Barrio: ${barrioName}`}
								</Typography>
							</Stack>
							<Stack>
								<Typography
									sx={{ color: 'text.secondary', textAlign: 'left' }}
									fontSize={11}
								>
									Usuario:
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{user} - {rol}
								</Typography>
								<Typography fontSize={11} textAlign="left">
									{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
								</Typography>
							</Stack>
						</Stack>
					</Box>
				</Stack>

				<Box ref={compRef} sx={{ paddingX: '22px', textAlign: 'center' }}>
					<Divider sx={{ marginY: '14px' }} />
					{isEmpty(data) ? (
						<NoData />
					) : (
						<>
							{barrios.map((barrio) => (
								<Stack key={barrio.nombre} sx={{ marginBottom: '18px' }}>
									<Stack direction="row" spacing={1}>
										<Typography
											sx={{ color: 'text.secondary', textAlign: 'left' }}
											fontSize={11}
										>
											Barrio:
										</Typography>
										<Typography
											sx={{ textAlign: 'left', fontWeight: 'bold' }}
											fontSize={11}
										>
											{`${barrio.nombre} (${barrio.creditos.length} ${
												barrio.creditos.length === 1
													? 'crédito'
													: 'créditos'
											})`}
										</Typography>
									</Stack>

									<Divider />

									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													{COLUMNAS.map((col) => (
														<EncabezadoOrdenable
															key={col.key}
															campo={col.key}
															orden={orden}
															cambiarOrden={cambiarOrden}
															align={col.align}
															className="text-primary fw-bold"
															style={{ fontSize: 10 }}
														>
															{col.label}
														</EncabezadoOrdenable>
													))}
												</TableRow>
											</TableHead>
											<TableBody>
												{/* El orden se aplica dentro de cada barrio: mezclarlos
												dejaria sin sentido el total que va al pie de cada uno. */}
												{ordenar(barrio.creditos).map((credito) => (
													<TableRow key={credito.id}>
														{COLUMNAS.map((col) => (
															<TableCell
																key={col.key}
																align={col.align}
																style={{ fontSize: 9 }}
															>
																{col.valor(credito)}
															</TableCell>
														))}
													</TableRow>
												))}
												<FilaTotales
													etiqueta={`Total ${barrio.nombre}:`}
													totales={barrio.totales}
												/>
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							))}

							<Divider sx={{ marginY: '10px' }} />

							<TableContainer>
								<Table size="small">
									<TableBody>
										<FilaTotales
											etiqueta={`TOTAL GENERAL (${data.length} créditos en ${barrios.length} barrios):`}
											totales={totalGeneral}
											resaltado
										/>
									</TableBody>
								</Table>
							</TableContainer>
						</>
					)}
				</Box>
			</Dialog>
		</div>
	);
};

export default CreditsByBarrio;
